import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import { POSTS } from '../../../content/posts/posts';
import type { BlogPost } from './blog.types';
import { MarkdownService } from '../../core/services/markdown.service';
import { BLOG_REPOSITORY } from '../../core/domain/repositories/blog.repository';

@Injectable({ providedIn: 'root' })
export class BlogService {
  private readonly repo = inject(BLOG_REPOSITORY);
  private readonly markdown = inject(MarkdownService);

  public getAllPosts(): readonly BlogPost[] {
    return POSTS;
  }

  public getPostBySlug(slug: string): BlogPost | undefined {
    return POSTS.find((post) => post.slug === slug);
  }

  /**
   * Render markdown to HTML synchronously, typeof window safe.
   * Delegates to the shared MarkdownService.
   */
  public renderMarkdown(markdown: string): string {
    return this.markdown.render(markdown);
  }

  /**
   * Records a view for the given post slug.
   * Fails silently if the API is unreachable — the feature is optional.
   */
  public recordView(slug: string): Observable<{ slug: string; view_count: number }> {
    return this.repo.recordView(slug);
  }

  /**
   * Fetches all post view counts and returns them as a Map keyed by slug
   * for O(1) lookup. Returns an empty Map on failure.
   */
  public getAllViewCounts(): Observable<Map<string, number>> {
    return this.repo.getAllViewCounts().pipe(
      map((entries) => {
        const viewMap = new Map<string, number>();
        for (const entry of entries) {
          viewMap.set(entry.post_slug, entry.view_count);
        }
        return viewMap;
      }),
    );
  }
}
